import { useAtom, useSetAtom } from 'jotai';
import React, { ReactNode, createContext, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import apiManager from '@/api/AxiosInstance';
import AlertModal from '@/components/Common/Modal/AlertModal';
import { REPRESENTATIVE_NUMBER } from '@/const/common';
import { isExistNotReadChatMessageAtom } from '@/store/chatting';
import { getMemberAtom } from '@/store/member';
import { isExistNotReadNotificationAtom } from '@/store/notification';
import { formatYYYYMMDD } from '@/utils/common';
import newApiManager from '@/api/NewAxiosInstance';

type MemberContextType = {
  member: Member | null;
  login: (member: Member) => void;
  isLoading?: boolean | undefined;
  logout: () => void;
  updateMember: (member: Member) => void;
  removeMember: () => void;
  updateNotificationSettings: (key: string, checked: boolean | number) => void;
  getMember?: (id: number) => void;
  updateDriveVehicleInfo: (data: DriveVehicleInfo) => void;
  updateReferralCodes: (data: ReferralCode) => void;
};

const MemberContext = createContext<MemberContextType | null>(null);

const MemberProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false);
  const [alertModalContent, setAlertModalContent] = useState<string>('');
  const [modifiedDate, setModifiedDate] = useState<string>('');
  const [isDealerSuspended, setIsDealerSuspended] = useState(false);

  const navigate = useNavigate();
  const [, getMemberInfoAtom] = useAtom(getMemberAtom);

  const setIsExistNotReadNotification = useSetAtom(isExistNotReadNotificationAtom);
  const setIsExistNotReadChatMessage = useSetAtom(isExistNotReadChatMessageAtom);

  const [member, setMember] = useState<Member | null>(null);

  const login = (member: Member) => {
    setMember(member);
  };

  const logout = () => {
    setMember(null);
  };

  const updateMember = (member: Member) => {
    setMember(member);
  };

  const removeMember = () => {
    setMember(null);
  };

  const updateNotificationSettings = (key: string, checked: boolean | number) => {
    setMember((prevMember) => {
      if (!prevMember) return null;

      const updatedSettings = {
        ...prevMember.notificationSettings,
        [key]: checked,
      };

      const updatedMember = {
        ...prevMember,
        notificationSettings: updatedSettings,
      };

      return updatedMember;
    });
  };

  const updateDriveVehicleInfo = (data: DriveVehicleInfo) => {
    setMember((prevMember) => {
      if (!prevMember) return null;

      return { ...prevMember, driveVehicleInfo: { ...prevMember.driveVehicleInfo, ...data } };
    });
  };

  const updateReferralCodes = (data: ReferralCode) => {
    setMember((prevMember) => {
      if (!prevMember) return null;

      return { ...prevMember, referralCodes: [...prevMember.referralCodes, data] };
    });
  };

  const getMember = (id: number) => {
    newApiManager
      .get(`/api/v1/members/${id}`)
      .then((response) => {
        login(response.data);
        setIsExistNotReadNotification(response.data.isExistNotReadNotification);
        setIsExistNotReadChatMessage(response.data.isExistNotReadChatMessage);
        setModifiedDate(formatYYYYMMDD(response.data.modifiedDate));

        // member아톰에 넣어주기
        getMemberInfoAtom(response.data);

        if (!!response.data.deleted && !!response.data.deleteReason) {
          setAlertModalContent(response.data.deleteReason);
          setIsDealerSuspended(response.data.type?.code !== 'NORMAL');
          setIsOpenAlertModal(true);
        }

        if (!!response.data.needChangePhoneNumber) {
          navigate('/member/phone-number', { state: { needChangePhoneNumber: response.data.needChangePhoneNumber } });
        }
      })
      .catch((error) => {
        localStorage.removeItem('token');
        localStorage.removeItem('driveTutorial');
        localStorage.removeItem('driveTutorialReceivedCheck');
      });
  };

  const handleClose = () => {
    setIsOpenAlertModal(false);
    localStorage.removeItem('token');
    localStorage.removeItem('driveTutorial');
    localStorage.removeItem('driveTutorialReceivedCheck');
    logout();
    navigate('/login');
  };

  return (
    <MemberContext.Provider
      value={{
        member,
        login,
        logout,
        updateMember,
        removeMember,
        updateNotificationSettings,
        getMember,
        updateDriveVehicleInfo,
        updateReferralCodes,
      }}
    >
      {children}
      <AlertModal
        isOpen={isOpenAlertModal}
        content={
          <>
            {!isDealerSuspended && (
              <p className="font-semibold text-[20px] mb-6">
                운영 정책 위반으로
                <br />
                계정이 정지되었습니다
              </p>
            )}
            <p>
              {alertModalContent}
              <br />
              {REPRESENTATIVE_NUMBER}
              <br />
              {modifiedDate}
            </p>
          </>
        }
        onClose={handleClose}
      />
    </MemberContext.Provider>
  );
};

export const useMemberContext = () => {
  const context = useContext(MemberContext) as MemberContextType;
  if (context === undefined) {
    throw new Error('useMemberContext must be used within a MemberProvider');
  }
  return context;
};

export { MemberContext, MemberProvider };
